import React, { useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Select, TableShell, TagBadge } from '../../../components/ui'
import Icon from '../../../components/Icon'
import { parseAmount } from '../../../utils/formatRub'
import { isArtel, isHired } from './incomeReportCalculations'

export const REPORT_TYPE_INCOME = 'income_quarterly'
export const REPORT_TYPE_SUMMARY_BUDGET = 'summary_budget'
export const REPORT_TYPE_LABELS = {
  [REPORT_TYPE_INCOME]: 'Квартальный отчет по доходам',
  [REPORT_TYPE_SUMMARY_BUDGET]: 'Сводный бюджет',
}

export function ReportStatusBadge({ report }) {
  if (report?.type !== REPORT_TYPE_INCOME) return null
  return <TagBadge variant={report.is_primary ? 'success' : 'grey'}>{report.is_primary ? 'Основной' : 'Черновик'}</TagBadge>
}

const OVERRIDE_FIELD_BY_COLUMN = {
  sharePercent: 'share_percent',
  totalAfterTax: 'total_after_tax',
  toReceive: 'to_receive',
  salary: 'salary',
  cashRounded: 'cash_rounded',
  cashDistribution: 'cash_payment',
  cashDeskSupplement: 'cash_payment',
}

export const BASE_COLUMNS = [
  { id: 'num', label: '№', className: 'center', width: 'var(--report-sticky-num-width)', sticky: 'num' },
  { id: 'team_name', label: 'Команда', sortable: true, width: 'var(--report-sticky-team-width)', sticky: 'team' },
  { id: 'employee_name', label: 'ФИО Сотрудника', sortable: true, width: 'var(--report-sticky-name-width)', sticky: 'name' },
  { id: 'employee_type', label: 'Тип', sortable: true },
  { id: 'sharePercent', label: '% пая', numeric: true, sortable: true, editable: 'artel', format: 'percent' },
  { id: 'otherParticipation', label: '% иного участия', numeric: true, sortable: true, format: 'fractionPercent' },
  { id: 'actsSum', label: 'Заработано', numeric: true, sortable: true, format: 'money' },
  { id: 'toReceive', label: 'К получению', numeric: true, sortable: true, editable: true, format: 'money' },
  { id: 'delta', label: 'Дельта', numeric: true, sortable: true, format: 'money' },
  { id: 'salary', label: 'Зарплата', numeric: true, sortable: true, editable: 'hired', format: 'money' },
  { id: 'sharePayment', label: 'Выплата по паевому', numeric: true, sortable: true, format: 'money' },
  { id: 'laborPay', label: 'Выплата по трудовому', numeric: true, sortable: true, format: 'money' },
  { id: 'otherPayment', label: 'Выплата по иному', numeric: true, sortable: true, format: 'money' },
  { id: 'maxOther', label: 'Макс. по иному', numeric: true, sortable: true, hiddenKey: 'maxOther', lavender: true, format: 'money' },
  { id: 'realOther', label: 'По иному реальная', numeric: true, sortable: true, hiddenKey: 'realOther', lavender: true, format: 'money' },
  { id: 'cash', label: 'Наличка', numeric: true, sortable: true, hiddenKey: 'cash', lavender: true, format: 'money' },
  { id: 'cashRounded', label: 'Наличка (окр.)', numeric: true, sortable: true, hiddenKey: 'cashRounded', format: 'money' },
  { id: 'otherRounded', label: 'По иному (окр.)', numeric: true, sortable: true, hiddenKey: 'otherRounded', format: 'money' },
  { id: 'cashPotential', label: 'Потенциал по наличке', numeric: true, sortable: true, hiddenKey: 'cashPotential', lavender: true, format: 'money' },
  { id: 'cashDistribution', label: 'Распределение нала', numeric: true, sortable: true, editable: true, green: true, format: 'money' },
  { id: 'cashDeskSupplement', label: 'Доплата из кассы', numeric: true, sortable: true, editable: true, green: true, format: 'money' },
  { id: 'totalBeforeTax', label: 'Итого до НДФЛ', numeric: true, sortable: true, format: 'money' },
  { id: 'totalAfterTax', label: 'Итого после НДФЛ', numeric: true, sortable: true, editable: true, format: 'money' },
]

export function toNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === '') return fallback
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback
  const parsed = parseAmount(value)
  return parsed === null ? fallback : parsed
}

export function formatMoney(value, { empty = '—', showZero = true } = {}) {
  const n = toNumber(value, null)
  if (n === null) return empty
  if (n === 0 && !showZero) return empty
  return `${Math.round(n).toLocaleString('ru-RU')} ₽`
}

export function formatPercentValue(value, { fraction = false, fractionDigits = 2 } = {}) {
  const n = toNumber(value, null)
  if (n === null) return '—'
  const normalized = fraction ? n * 100 : n
  if (normalized === 0) return '—'
  const roundingFactor = 10 ** fractionDigits
  const rounded = Math.round((normalized + 1e-12) * roundingFactor) / roundingFactor
  return `${new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(rounded)}%`
}

export function formatDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function makeClientId(prefix = 'row') {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function reportTitle(report) {
  return report?.title || REPORT_TYPE_LABELS[report?.type] || 'Отчет'
}

export function MoneyInput({
  value,
  onChange,
  onEnter,
  readOnly = false,
  overridden = false,
  className = '',
  ariaLabel,
  inputProps = {},
}) {
  const [focused, setFocused] = useState(false)
  const [text, setText] = useState(String(Math.round(toNumber(value))))

  React.useEffect(() => {
    if (!focused) {
      setText(String(Math.round(toNumber(value))))
    }
  }, [focused, value])

  if (readOnly) {
    return <span className="report-money-static">{formatMoney(value)}</span>
  }

  const commitText = () => {
    const parsed = Math.round(toNumber(text))
    setText(String(parsed))
    setFocused(false)
    onChange?.(parsed)
  }

  return (
    <Input
      {...inputProps}
      aria-label={ariaLabel}
      className={`report-money-input${overridden ? ' report-money-input--overridden' : ''}${className ? ` ${className}` : ''}`}
      value={focused ? text : formatMoney(value)}
      onFocus={(event) => {
        const raw = String(Math.round(toNumber(value)))
        setText(raw)
        setFocused(true)
        event.target.select()
      }}
      onChange={(event) => setText(event.target.value)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return
        event.preventDefault()
        commitText()
        onEnter?.(event)
      }}
      onBlur={commitText}
    />
  )
}

export function PercentInput({ value, onChange, onEnter, overridden = false, ariaLabel, inputProps = {} }) {
  const [focused, setFocused] = useState(false)
  const [text, setText] = useState(String(toNumber(value)).replace('.', ','))

  React.useEffect(() => {
    if (!focused) {
      setText(String(toNumber(value)).replace('.', ','))
    }
  }, [focused, value])

  const commitText = () => {
    const parsed = toNumber(text, toNumber(value))
    const normalized = Math.min(100, Math.max(0, Math.round((parsed + 1e-12) * 100) / 100))
    setText(String(normalized).replace('.', ','))
    setFocused(false)
    onChange?.(normalized)
  }

  return (
    <Input
      {...inputProps}
      aria-label={ariaLabel}
      className={`report-money-input report-percent-input${overridden ? ' report-money-input--overridden' : ''}`}
      inputMode="decimal"
      value={focused ? text : formatPercentValue(value)}
      onFocus={(event) => {
        setText(String(toNumber(value)).replace('.', ','))
        setFocused(true)
        event.target.select()
      }}
      onChange={(event) => setText(event.target.value)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return
        event.preventDefault()
        commitText()
        onEnter?.(event)
      }}
      onBlur={commitText}
    />
  )
}

export function EditableReportTitle({ report, readOnly, onSave, className = '', fallback = 'Отчет' }) {
  const currentTitle = report ? reportTitle(report) : fallback
  const [draft, setDraft] = useState(currentTitle)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  React.useEffect(() => {
    setDraft(currentTitle)
  }, [currentTitle])

  if (readOnly) {
    return <h1 className={className}>{currentTitle}</h1>
  }

  const commitTitle = async () => {
    const nextTitle = draft.trim()
    if (!nextTitle) {
      setDraft(currentTitle)
      setError('Название отчета не может быть пустым')
      return
    }
    if (nextTitle === currentTitle || saving) return
    setSaving(true)
    setError('')
    try {
      await onSave?.(nextTitle)
    } catch (saveError) {
      setDraft(currentTitle)
      setError(saveError?.response?.data?.error || 'Не удалось сохранить название отчета')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className={`report-title-editor${className ? ` ${className}` : ''}`}
      role="heading"
      aria-level="1"
    >
      <Input
        aria-label="Название отчета"
        className="report-title-editor__input"
        value={draft}
        disabled={saving}
        maxLength={255}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commitTitle}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') {
            setDraft(currentTitle)
            event.currentTarget.blur()
          }
        }}
      />
      {error && <span className="report-title-editor__error" role="alert">{error}</span>}
    </div>
  )
}

function getColumnValue(row, columnId) {
  if (columnId === 'team_name') return row.team_name || ''
  if (columnId === 'employee_name') return row.employee_name || ''
  if (columnId === 'employee_type') return row.employee_type || ''
  return row[columnId]
}

function compareValues(a, b) {
  if (a === null || a === undefined) return b === null || b === undefined ? 0 : 1
  if (b === null || b === undefined) return -1
  if (typeof a === 'string' || typeof b === 'string') {
    return String(a).localeCompare(String(b), 'ru')
  }
  return toNumber(a) - toNumber(b)
}

function visibleColumns(showCols) {
  return BASE_COLUMNS.filter((column) => !column.hiddenKey || showCols[column.hiddenKey])
}

function groupRows(rows, grouping, sort, mainSort = 'team') {
  let sortedRows = [...rows]
  if (sort?.column) {
    const dir = sort.direction === 'desc' ? -1 : 1
    sortedRows.sort((a, b) => compareValues(getColumnValue(a, sort.column), getColumnValue(b, sort.column)) * dir)
  } else if (mainSort === 'name') {
    sortedRows.sort((a, b) => {
      const name = String(a.employee_name || '').localeCompare(String(b.employee_name || ''), 'ru')
      return name || String(a.team_name || '').localeCompare(String(b.team_name || ''), 'ru')
    })
  } else {
    sortedRows.sort((a, b) => {
      const team = String(a.team_name || '').localeCompare(String(b.team_name || ''), 'ru')
      return team || String(a.employee_name || '').localeCompare(String(b.employee_name || ''), 'ru')
    })
  }

  if (grouping === 'none') return [{ key: null, rows: sortedRows }]
  const getKey = grouping === 'type'
    ? (row) => row.employee_type || 'Без типа'
    : (row) => row.team_name || 'Без команды'
  const map = new Map()
  sortedRows.forEach((row) => {
    const key = getKey(row)
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(row)
  })
  return [...map.entries()]
    .sort(([a], [b]) => String(a).localeCompare(String(b), 'ru'))
    .map(([key, group]) => ({ key, rows: group }))
}

function overrideMap(overrides) {
  const map = new Map()
  ;(overrides || []).forEach((override) => {
    map.set(`${override.row_key}:${override.field}`, override)
  })
  return map
}

function defaultOverrideValue(row, field) {
  if (field === 'share_percent') return row.sharePercentDefault
  if (field === 'to_receive') return row.toReceiveDefault
  if (field === 'cash_rounded') return row.cashRoundedDefault
  if (field === 'salary') return row.salaryDefault
  return 0
}

function overrideValueFromColumn(row, column, value) {
  if (column.id === 'sharePercent') {
    return Math.round((toNumber(value) + 1e-12) * 100) / 100
  }
  const normalized = Math.round(toNumber(value))
  if (column.id === 'cashDistribution') {
    if (normalized === Math.round(toNumber(row.cashDistribution))) {
      return Math.round(toNumber(row.cashPayment))
    }
    return Math.round(toNumber(row.cashRounded)) + normalized
  }
  if (column.id === 'cashDeskSupplement') {
    if (normalized === Math.round(toNumber(row.cashDeskSupplement))) {
      return Math.round(toNumber(row.cashPayment))
    }
    return Math.round(toNumber(row.cashRounded)) - normalized
  }
  return normalized
}

function canEditColumn(row, column) {
  if (!column.editable) return false
  if (column.editable === 'artel') return isArtel(row.employee_type)
  if (column.editable === 'hired') return isHired(row.employee_type)
  return true
}

function stickyColumnClass(column) {
  if (!column.sticky) return ''
  const stickyKey = typeof column.sticky === 'string' ? column.sticky : 'name'
  return [
    'report-sticky-col',
    `report-sticky-col--${stickyKey}`,
    stickyKey === 'name' ? 'report-sticky-name' : '',
  ].filter(Boolean).join(' ')
}

function formatCell(row, column) {
  const value = getColumnValue(row, column.id)
  if (column.format === 'money') return formatMoney(value)
  if (column.format === 'percent') return formatPercentValue(value)
  if (column.format === 'fractionPercent') return formatPercentValue(value, { fraction: true })
  if (column.id === 'num') return ''
  return value || '—'
}

export function ReportInfoCards({ result }) {
  const cards = [
    { key: 'payFund', label: 'Паевой фонд', value: result.infoCards.payFund, tone: 'green' },
    { key: 'cashRoundedTotal', label: 'Наличка (окр.) итого', value: result.infoCards.cashRoundedTotal, tone: 'blue' },
    { key: 'additionalBalance', label: 'Баланс доп. расходов', value: result.infoCards.additionalBalance, tone: result.infoCards.additionalBalance < 0 ? 'orange' : 'gray' },
    { key: 'totalCashNeeded', label: 'Нужно наличкой всего', value: result.infoCards.totalCashNeeded, tone: result.infoCards.cashDelta > 0 ? 'red' : 'green' },
    { key: 'cashPaid', label: 'Выплачено наличкой', value: result.infoCards.cashPaid, tone: 'gray' },
    { key: 'cashDelta', label: result.infoCards.cashDelta > 0 ? 'Ещё нужно' : 'Покрыто', value: Math.abs(result.infoCards.cashDelta), tone: result.infoCards.cashDelta > 0 ? 'red' : 'green' },
  ]

  return (
    <div className="report-info-grid">
      {cards.map((card) => (
        <div key={card.key} className={`report-info-card report-info-card--${card.tone}`}>
          <div className="report-info-card__label">{card.label}</div>
          <div className="report-info-card__value">{formatMoney(card.value)}</div>
        </div>
      ))}
    </div>
  )
}

const PNL_MONEY_LABELS = {
  fund_start: 'Фонд содержания (начало периода)',
  income_acts: 'Доход по актам за период',
  tax_fact: 'Налоги и обязательные платежи, факт',
  tax_plan: 'Налоги и обязательные платежи, план',
  other_exp_fact: 'Другие расходы, факт',
  other_exp_plan: 'Другие расходы, план',
  profit_distribution: 'Прибыль к распределению (= К получению)',
}

export function ReportPnlSummary({ result, config, onConfigChange, readOnly }) {
  const [collapsed, setCollapsed] = useState(true)
  const mainIncomes = (config.expenses || []).filter((item) => item.category === 'main' && item.type === 'income')
  const mainExpenses = (config.expenses || []).filter((item) => item.category === 'main' && item.type === 'expense')

  const moneyField = (field) => (
    <MoneyInput
      value={config[field] || 0}
      readOnly={readOnly}
      onChange={(next) => onConfigChange?.({ ...config, [field]: next })}
      ariaLabel={PNL_MONEY_LABELS[field] || field}
    />
  )

  const profitDistributionField = (
    <MoneyInput
      value={result.pnl.profitDistribution}
      readOnly={readOnly}
      onChange={(next) => onConfigChange?.({ ...config, profit_distribution: next })}
      ariaLabel={config.payment_summary ? 'Выплаты из официального фонда' : PNL_MONEY_LABELS.profit_distribution}
    />
  )

  const diffClass = (value) => value >= 0 ? 'report-pnl__diff-pos' : 'report-pnl__diff-neg'

  return (
    <section className={`report-pnl${collapsed ? ' report-pnl--collapsed' : ''}${config.payment_summary ? ' report-pnl--payment-types' : ''}`}>
      <button
        type="button"
        className="report-pnl__toggle"
        onClick={() => setCollapsed((value) => !value)}
        aria-expanded={!collapsed}
        aria-controls="income-report-pnl-body"
      >
        <span className="report-pnl__arrow" aria-hidden="true">▼</span>
        <span>Сводка</span>
      </button>
      {(!collapsed || config.payment_summary) && (
        <div className="report-pnl__body" id="income-report-pnl-body" hidden={collapsed}>
          <table className="report-pnl__table">
            <tbody>
              <tr>
                <td><strong>Фонд содержания (начало периода)</strong></td>
                <td className="report-num" colSpan={3}>{moneyField('fund_start')}</td>
              </tr>
              <tr className="report-pnl__section"><td colSpan={4}>ДОХОДЫ</td></tr>
              <tr>
                <td>Доход по актам за период</td>
                <td className="report-num" colSpan={3}>{moneyField('income_acts')}</td>
              </tr>
              {mainIncomes.map((item) => (
                <tr key={item.id || item.title}>
                  <td>{item.title || 'Доход'}</td>
                  <td className="report-num" colSpan={3}>{formatMoney(item.amount)}</td>
                </tr>
              ))}
              <tr className="report-pnl__subtotal">
                <td>Итого доходов</td>
                <td className="report-num" colSpan={3}>{formatMoney(result.pnl.totalIncome)}</td>
              </tr>
              <tr className="report-pnl__subtotal">
                <td>Итого на конец квартала</td>
                <td className="report-num" colSpan={3}>{formatMoney(result.pnl.endBalance)}</td>
              </tr>
              <tr className="report-pnl__section">
                <td>РАСХОДЫ</td>
                <td className="report-num">Разница</td>
                <td className="report-num">План</td>
                <td className="report-num">Факт</td>
              </tr>
              <tr>
                <td>Налоги и обяз. платежи</td>
                <td className={`report-num ${diffClass(result.pnl.taxDiff)}`}>{formatMoney(result.pnl.taxDiff)}</td>
                <td className="report-num">{moneyField('tax_plan')}</td>
                <td className="report-num">{moneyField('tax_fact')}</td>
              </tr>
              <tr>
                <td>Другие расходы</td>
                <td className={`report-num ${diffClass(result.pnl.otherExpenseDiff)}`}>{formatMoney(result.pnl.otherExpenseDiff)}</td>
                <td className="report-num">{moneyField('other_exp_plan')}</td>
                <td className="report-num">{moneyField('other_exp_fact')}</td>
              </tr>
              {config.payment_summary && <tr>
                <td>Внутренние работы за счёт артели</td><td className="report-num">—</td><td className="report-num">—</td>
                <td className="report-num">{formatMoney(result.pnl.artelExpense)}</td>
              </tr>}
              {mainExpenses.map((item) => (
                <tr key={item.id || item.title}>
                  <td>{item.title || 'Расход'}</td>
                  <td className="report-num">—</td>
                  <td className="report-num">—</td>
                  <td className="report-num">{formatMoney(item.amount)}</td>
                </tr>
              ))}
              <tr className="report-pnl__subtotal">
                <td>Сумма расходов</td>
                <td className={`report-num ${diffClass(result.pnl.totalExpensesDiff)}`}>{formatMoney(result.pnl.totalExpensesDiff)}</td>
                <td className="report-num">{formatMoney(result.pnl.totalExpensesPlan)}</td>
                <td className="report-num">{formatMoney(result.pnl.totalExpensesFact)}</td>
              </tr>
              <tr className={result.pnl.profit >= 0 ? 'report-pnl__grandtotal' : 'report-pnl__negative'}>
                <td>Общая прибыль</td>
                <td className="report-num" colSpan={3}>{formatMoney(result.pnl.profit)}</td>
              </tr>
              <tr className="report-pnl__subtotal">
                <td>{config.payment_summary ? 'Выплаты из официального фонда' : 'Прибыль к распределению (= К получению)'}</td>
                <td className="report-num" colSpan={3}>{profitDistributionField}</td>
              </tr>
              <tr className={result.pnl.fundEnd >= 0 ? 'report-pnl__grandtotal' : 'report-pnl__negative'}>
                <td>Фонд содержания (конец периода)</td>
                <td className="report-num" colSpan={3}>{formatMoney(result.pnl.fundEnd)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export function ReportDataTable({
  result,
  overrides,
  onOverridesChange,
  readOnly,
  grouping,
  onGroupingChange,
  mainSort,
  onMainSortChange,
  showCols,
  onShowColsChange,
  sort,
  onSortChange,
  summary = null,
}) {
  const [columnsOpen, setColumnsOpen] = useState(false)
  const tableRef = useRef(null)
  const extraColsPanelId = useId()
  const columns = useMemo(() => visibleColumns(showCols), [showCols])
  const groups = useMemo(() => groupRows(result.rows, grouping, sort, mainSort), [result.rows, grouping, sort, mainSort])
  const overridesByKey = useMemo(() => overrideMap(overrides), [overrides])
  const cashCovered = result.infoCards.cashCovered

  const setOverride = (row, column, value) => {
    const field = OVERRIDE_FIELD_BY_COLUMN[column.id]
    if (!field) return
    const defaultValue = defaultOverrideValue(row, field)
    const normalized = overrideValueFromColumn(row, column, value)
    onOverridesChange((prev) => {
      const rest = (prev || []).filter((item) => !(item.row_key === row.row_key && item.field === field))
      const defaultNormalized = column.id === 'sharePercent'
        ? Math.round((toNumber(defaultValue) + 1e-12) * 100) / 100
        : Math.round(toNumber(defaultValue))
      if (normalized === defaultNormalized) return rest
      return [
        ...rest,
        {
          row_key: row.row_key,
          employee_id: row.employee_id || null,
          field,
          value: normalized,
        },
      ]
    })
  }

  const handleSort = (column) => {
    if (!column.sortable) return
    if (sort?.column === column.id) {
      onSortChange({ column: column.id, direction: sort.direction === 'asc' ? 'desc' : 'asc' })
    } else {
      onSortChange({ column: column.id, direction: 'asc' })
    }
  }

  const setMainSortMode = (value) => {
    onMainSortChange?.(value)
  }

  const focusNextEditableInput = (columnId, currentRowIndex) => {
    window.setTimeout(() => {
      const root = tableRef.current
      if (!root) return
      const inputs = Array.from(root.querySelectorAll('input[data-report-editable-input="true"]'))
      const nextInput = inputs.find((input) => (
        input.dataset.reportColumnId === columnId
        && Number(input.dataset.reportRowIndex) > currentRowIndex
        && !input.disabled
        && !input.readOnly
      ))
      if (!nextInput) return
      nextInput.focus()
      nextInput.select?.()
    }, 0)
  }

  const grandTotals = result.grandTotals || result.totals
  let rowNumber = 0
  const stickyGroupColumns = columns.slice(0, 3)
  const groupFillColSpan = Math.max(columns.length - stickyGroupColumns.length, 0)

  return (
    <div className="report-table-section">
      <div className="report-controls">
        <div className="report-control-group">
          <label className="report-control-label">Группировка:</label>
          <div className="report-toggle" role="group" aria-label="Группировка">
            {[
              ['none', 'Нет'],
              ['team', 'По командам'],
              ['type', 'По типу'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`report-toggle__btn${grouping === value ? ' report-toggle__btn--active' : ''}`}
                onClick={() => onGroupingChange?.(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="report-control-group">
          <label className="report-control-label">Сортировка:</label>
          <div className="report-toggle" role="group" aria-label="Сортировка">
            {[
              ['team', 'По команде'],
              ['name', 'По ФИО'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`report-toggle__btn${mainSort === value ? ' report-toggle__btn--active' : ''}`}
                onClick={() => setMainSortMode(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="report-control-group report-extra-cols">
          <button
            type="button"
            className="ui-btn ui-btn-secondary report-extra-cols__button"
            onClick={() => setColumnsOpen((value) => !value)}
            aria-expanded={columnsOpen}
            aria-controls={extraColsPanelId}
          >
            Доп. колонки{' '}
            <Icon name={columnsOpen ? 'chevronUp' : 'chevronDown'} size={14} aria-hidden="true" />
          </button>
          <div
            className="report-extra-cols__panel"
            id={extraColsPanelId}
            role="region"
            aria-label="Дополнительные колонки таблицы"
            hidden={!columnsOpen}
          >
            {[
              ['maxOther', 'Макс. по иному'],
              ['realOther', 'По иному реальная'],
              ['cash', 'Наличка'],
              ['cashRounded', 'Наличка (окр.)'],
              ['otherRounded', 'По иному (окр.)'],
              ['cashPotential', 'Потенциал по наличке'],
            ].map(([key, label]) => (
              <label key={key} className="report-check">
                <input
                  type="checkbox"
                  checked={Boolean(showCols[key])}
                  onChange={(event) => onShowColsChange({ ...showCols, [key]: event.target.checked })}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {summary ? <div className="report-pnl-above-table">{summary}</div> : null}

      <TableShell className="report-table-shell">
        <div className="report-table-wrap">
          <table className="report-data-table" ref={tableRef}>
            <thead>
              <tr>
                {columns.map((column) => {
                  const sorted = sort?.column === column.id
                  const className = [
                    column.className,
                    column.numeric ? 'report-num' : '',
                    column.editable ? 'report-col-editable' : '',
                    column.lavender ? 'report-col-lavender' : '',
                    column.green && cashCovered ? 'report-col-green' : '',
                    stickyColumnClass(column),
                    sorted ? 'report-col-sorted' : '',
                  ].filter(Boolean).join(' ')
                  const ariaSort = column.sortable
                    ? (sorted
                      ? (sort.direction === 'asc' ? 'ascending' : 'descending')
                      : 'none')
                    : undefined
                  return (
                    <th
                      key={column.id}
                      className={className}
                      style={column.width ? { width: column.width } : undefined}
                      scope="col"
                      aria-sort={ariaSort}
                    >
                      {column.sortable ? (
                        <button
                          type="button"
                          className="report-th-sort"
                          onClick={() => handleSort(column)}
                        >
                          {column.label}
                          {sorted && (
                            <span className="report-sort-arrow" aria-hidden>
                              {sort.direction === 'asc' ? ' ▲' : ' ▼'}
                            </span>
                          )}
                        </button>
                      ) : (
                        column.label
                      )}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <React.Fragment key={group.key || 'all'}>
                  {group.key && (
                    <tr className="report-group-row">
                      {stickyGroupColumns.map((column, index) => {
                        const classes = [
                          column.className,
                          stickyColumnClass(column),
                          index === 1 ? 'report-group-row__label' : '',
                        ].filter(Boolean).join(' ')
                        return (
                          <td key={column.id} className={classes}>
                            {index === 1 ? group.key : ''}
                          </td>
                        )
                      })}
                      {groupFillColSpan > 0 && (
                        <td className="report-group-row__fill" colSpan={groupFillColSpan} aria-hidden="true" />
                      )}
                    </tr>
                  )}
                  {group.rows.map((row) => {
                    rowNumber += 1
                    const currentRowNumber = rowNumber
                    return (
                      <tr key={row.row_key}>
                        {columns.map((column) => {
                          const field = OVERRIDE_FIELD_BY_COLUMN[column.id]
                          const canEdit = !readOnly && canEditColumn(row, column)
                          const override = field ? overridesByKey.get(`${row.row_key}:${field}`) : null
                          const cellValue = column.id === 'num' ? currentRowNumber : getColumnValue(row, column.id)
                          const classes = [
                            column.className,
                            column.numeric ? 'report-num' : '',
                            column.editable ? 'report-col-editable' : '',
                            column.lavender ? 'report-col-lavender' : '',
                            column.green && cashCovered ? 'report-col-green' : '',
                            stickyColumnClass(column),
                            toNumber(cellValue) < 0 && column.numeric ? 'report-negative' : '',
                          ].filter(Boolean).join(' ')
                          return (
                            <td key={column.id} className={classes}>
                              {canEdit ? (
                                column.id === 'sharePercent' ? (
                                  <PercentInput
                                    value={cellValue}
                                    overridden={Boolean(override)}
                                    onChange={(next) => setOverride(row, column, next)}
                                    onEnter={() => focusNextEditableInput(column.id, currentRowNumber)}
                                    ariaLabel={`${column.label}: ${row.employee_name}`}
                                    inputProps={{
                                      'data-report-editable-input': 'true',
                                      'data-report-column-id': column.id,
                                      'data-report-row-index': currentRowNumber,
                                    }}
                                  />
                                ) : (
                                  <MoneyInput
                                    value={cellValue}
                                    overridden={Boolean(override)}
                                    onChange={(next) => setOverride(row, column, next)}
                                    onEnter={() => focusNextEditableInput(column.id, currentRowNumber)}
                                    ariaLabel={`${column.label}: ${row.employee_name}`}
                                    inputProps={{
                                      'data-report-editable-input': 'true',
                                      'data-report-column-id': column.id,
                                      'data-report-row-index': currentRowNumber,
                                    }}
                                  />
                                )
                              ) : (
                                column.id === 'num' ? currentRowNumber : formatCell(row, column)
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </React.Fragment>
              ))}
              {result.rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="report-empty-cell">Нет строк в снимке отчета</td>
                </tr>
              )}
              {result.rows.length > 0 && (
                <tr className="report-total-row">
                  {columns.map((column) => {
                    const classes = [
                      column.numeric ? 'report-num' : '',
                      column.editable ? 'report-col-editable' : '',
                      column.lavender ? 'report-col-lavender' : '',
                      column.green && cashCovered ? 'report-col-green' : '',
                      stickyColumnClass(column),
                    ].filter(Boolean).join(' ')
                    return <td key={column.id} className={classes} />
                  })}
                </tr>
              )}
              {(result.rows.length > 0 || toNumber(result.miscExpenseRow?.toReceive) > 0) && (
                <tr className="report-misc-expense-row">
                  {columns.map((column) => {
                    const cellValue = column.id === 'num' ? '' : getColumnValue(result.miscExpenseRow, column.id)
                    const classes = [
                      column.className,
                      column.numeric ? 'report-num' : '',
                      column.lavender ? 'report-col-lavender' : '',
                      column.green && cashCovered ? 'report-col-green' : '',
                      stickyColumnClass(column),
                      toNumber(cellValue) < 0 && column.numeric ? 'report-negative' : '',
                    ].filter(Boolean).join(' ')
                    if (column.id === 'num') return <td key={column.id} className={classes} />
                    if (column.id === 'employee_name') return <td key={column.id} className={classes}><strong>Прочие расходы</strong></td>
                    if (!column.numeric) return <td key={column.id} className={classes} />
                    if (column.format === 'percent' || column.format === 'fractionPercent') return <td key={column.id} className={classes} />
                    return <td key={column.id} className={classes}>{formatMoney(cellValue)}</td>
                  })}
                </tr>
              )}
              {(result.rows.length > 0 || toNumber(result.miscExpenseRow?.toReceive) > 0) && (
                <tr className="report-total-row report-grand-total-row">
                  {columns.map((column) => {
                    const classes = [
                      column.numeric ? 'report-num' : '',
                      column.editable ? 'report-col-editable' : '',
                      column.lavender ? 'report-col-lavender' : '',
                      column.green && cashCovered ? 'report-col-green' : '',
                      stickyColumnClass(column),
                      toNumber(grandTotals[column.id]) < 0 && column.numeric ? 'report-negative' : '',
                    ].filter(Boolean).join(' ')
                    if (column.id === 'num') return <td key={column.id} className={classes} />
                    if (column.id === 'employee_name') return <td key={column.id} className={classes}><strong>ИТОГО</strong></td>
                    if (!column.numeric) return <td key={column.id} className={classes} />
                    if (column.format === 'percent') {
                      return <td key={column.id} className={classes}>{formatPercentValue(grandTotals[column.id])}</td>
                    }
                    if (column.format === 'fractionPercent') {
                      return <td key={column.id} className={classes}>{formatPercentValue(grandTotals[column.id], { fraction: true })}</td>
                    }
                    const value = grandTotals[column.id] ?? grandTotals[column.id === 'actsSum' ? 'actsSum' : column.id]
                    return <td key={column.id} className={classes}>{formatMoney(value)}</td>
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </TableShell>
    </div>
  )
}

export function ReportToolbar({
  report,
  onSave,
  onRefresh,
  onCsv,
  onPrint,
  saving,
  refreshing,
  readOnly,
}) {
  const navigate = useNavigate()
  return (
    <div className="report-toolbar">
      <div className="report-toolbar__meta">
        <h1>{reportTitle(report)}</h1>
        <span>{report?.period_label || '—'}</span>
      </div>
      <div className="report-toolbar__actions">
        <Button variant="secondary" onClick={onCsv}>
          <Icon name="download" size={16} /> CSV
        </Button>
        <Button variant="secondary" onClick={onPrint}>
          <Icon name="download" size={16} /> PDF
        </Button>
        <Button variant="secondary" onClick={() => navigate(`/company/reports/${report?.id}/config`)}>
          <Icon name="settings" size={16} /> Конфигурация
        </Button>
        <Button variant="secondary" onClick={() => navigate(`/company/reports/${report?.id}/accountant`)}>
          <Icon name="fileCheck" size={16} /> Сформировать отчёт
        </Button>
        {!readOnly && (
          <>
            <Button variant="secondary" onClick={onRefresh} disabled={refreshing}>
              <Icon name="refresh" size={16} /> {refreshing ? 'Обновляю…' : 'Обновить из актов'}
            </Button>
            <Button variant="primary" onClick={onSave} disabled={saving}>
              {saving ? 'Сохранение…' : 'Сохранить'}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export function ReportLoadingState({ message = 'Загрузка отчета…' }) {
  return <div className="report-state">{message}</div>
}

export function ReportErrorState({ message, onRetry }) {
  return (
    <div className="report-state report-state--error">
      <p>{message || 'Не удалось загрузить отчет'}</p>
      {onRetry && <Button variant="secondary" onClick={onRetry}>Повторить</Button>}
    </div>
  )
}

export function ReportPageShell({ children }) {
  return (
    <div className="income-report-page">
      {children}
    </div>
  )
}

export function ConfigMoneyField({ label, value, onChange, readOnly }) {
  return (
    <label className="report-config-field">
      <span>{label}</span>
      <MoneyInput value={value || 0} onChange={onChange} readOnly={readOnly} ariaLabel={label} />
    </label>
  )
}

export function ConfigTextField({ label, value, onChange, readOnly, placeholder }) {
  return (
    <label className="report-config-field">
      <span>{label}</span>
      <Input value={value || ''} onChange={(event) => onChange(event.target.value)} disabled={readOnly} placeholder={placeholder} />
    </label>
  )
}

export function ConfigSelectField({ label, value, onChange, readOnly, children }) {
  return (
    <label className="report-config-field">
      <span>{label}</span>
      <Select value={value || ''} onChange={(event) => onChange(event.target.value)} disabled={readOnly}>
        {children}
      </Select>
    </label>
  )
}
